import { useState, useEffect, useRef } from "react";
import { Grid } from "@mui/material";
import Layout from "../../components/Layout";
import Button from "../../components/Button";
import robotAiImg from "../../assets/images/askai.png";
import useStyles from "./styles";
import { useNavigate, useParams } from "react-router-dom";
import { httprequest } from "../../data/api";
import { toast } from "react-toastify";
import PageLoader from "../../components/PageLoader";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { Icon } from "@iconify/react";
import Modal from "../../components/Modal";
import InputGroup from "../../components/FormComponent/InputGroup";
import RightDrawer from "../../components/RightDrawer";

const PracticeQuiz = () => {
  const { sessionId } = useParams();
  const classes = useStyles();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [sessionData, setSessionData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [timer, setTimer] = useState("00:00:00");
  const [percentage, setPercentage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timerIntervalRef = useRef(null);
  const totalTimeLimitRef = useRef(0);
  const isSubmittingRef = useRef(false);

  const [bookmarks, setBookmarks] = useState({});
  const [aiExplanation, setAiExplanation] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [isAiDrawerOpen, setIsAiDrawerOpen] = useState(false);

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [isReporting, setIsReporting] = useState(false);
  const [examPaused, setExamPaused] = useState(false);

  const handleSubmitExam = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      setLoading(true);
      await httprequest.post(`/api/v1/practice-sessions/${sessionId}/submit`);
      toast.success("Exam submitted successfully!");
      navigate(`/student/my-learning/practice-review/${sessionId}`);
    } catch (err) {
      toast.error("Failed to submit exam");
      setLoading(false);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const startTimer = (remainingSeconds, totalSeconds) => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);

    if (remainingSeconds <= 0) {
      setTimer("00:00:00");
      setPercentage(100);
      handleSubmitExam();

      return;
    }

    const deadline = Date.now() + remainingSeconds * 1000;

    timerIntervalRef.current = setInterval(() => {
      const remainingMs = Math.max(0, deadline - Date.now());

      const hours = Math.floor(remainingMs / (1000 * 60 * 60));
      const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);

      setTimer(
        `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
      );

      const usedMs = totalSeconds * 1000 - remainingMs;
      setPercentage(Math.min(100, Math.round((usedMs / (totalSeconds * 1000)) * 100)));

      if (remainingMs <= 0) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
        handleSubmitExam();
      }
    }, 1000);
  };

  useEffect(() => {
    const fetchSession = async () => {
      try {
        setLoading(true);
        const res = await httprequest.get(`/api/v1/practice-sessions/${sessionId}`);
        if (res?.data?.data) {
          const data = res.data.data;
          setSessionData(data);
          const loadedQuestions = data.questions || [];
          setQuestions(loadedQuestions);

          // Pre-fill already answered questions and bookmarks
          const answered = {};
          const initialBookmarks = {};
          loadedQuestions.forEach((q, idx) => {
            if (q.user_answer) answered[idx] = q.user_answer;
            if (q.is_bookmarked) initialBookmarks[idx] = true;
          });
          setAnsweredQuestions(answered);
          setBookmarks(initialBookmarks);

          // Setup timer if this is an exam
          if (data?.session?.mode === "exam" && data?.session?.time_limit_seconds != null) {
            const timeLimit = data.session.time_limit_seconds;
            const elapsed = data.session.elapsed_seconds_active || 0;
            const remainingSeconds = Math.max(0, timeLimit - elapsed);
            totalTimeLimitRef.current = timeLimit;
            setPercentage(Math.min(100, Math.round((elapsed / timeLimit) * 100)));
            startTimer(remainingSeconds, timeLimit);
          }
        }
      } catch (err) {
        toast.error("Failed to load session");
        navigate("/student/my-learning");
      } finally {
        setLoading(false);
      }
    };
    if (sessionId) fetchSession();
  }, [sessionId, navigate]);

  // Disable right-click and common inspect shortcuts
  useEffect(() => {
    const blockContextMenu = (e) => e.preventDefault();
    const blockKeys = (e) => {
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && ["I", "J", "C"].includes(e.key.toUpperCase())) ||
        (e.ctrlKey && e.key.toUpperCase() === "U")
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("keydown", blockKeys);

    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("keydown", blockKeys);
    };
  }, []);

  // Clear AI Explanation when question changes
  useEffect(() => {
    setAiExplanation(null);
  }, [currentQuestionIndex]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, []);

  const togglePauseResume = async () => {
    try {
      setLoading(true);
      if (examPaused) {
        // Resume: call API first, get fresh elapsed, restart timer
        const res = await httprequest.post(`/api/v1/practice-sessions/${sessionId}/resume`);
        setExamPaused(false);
        // Restart the timer with updated remaining time
        const totalLimit = totalTimeLimitRef.current;
        const elapsed = res?.data?.data?.session?.elapsed_seconds_active || res?.data?.data?.elapsed_seconds_active || 0;
        const remaining = Math.max(0, totalLimit - elapsed);
        startTimer(remaining, totalLimit);
        toast.success("Exam resumed.");
      } else {
        // Pause: stop local timer, call API
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        await httprequest.post(`/api/v1/practice-sessions/${sessionId}/pause`);
        setExamPaused(true);
        toast.success("Exam paused.");
      }
    } catch (err) {
      toast.error(`Failed to ${examPaused ? "resume" : "pause"} exam`);
    } finally {
      setLoading(false);
    }
  };

  // Auto-pause on unmount
  useEffect(() => {
    return () => {
      if (sessionData?.session?.mode === "exam" && !isSubmitting) {
        try {
          httprequest.post(`/api/v1/practice-sessions/${sessionId}/pause`).catch(() => {});
        } catch (e) {
          // ignore
        }
      }
    };
  }, [sessionId, sessionData, isSubmitting]);

  const currentSelectedAnswer = answeredQuestions[currentQuestionIndex];
  const totalQuestions = questions.length;

  const handleSelectOption = async (optionId) => {
    setAnsweredQuestions((prev) => ({
      ...prev,
      [currentQuestionIndex]: optionId
    }));

    const question = questions[currentQuestionIndex];
    if (question && question.id) {
      try {
        await httprequest.post(`/api/v1/practice-sessions/${sessionId}/answers`, {
          question_id: question.id,
          user_answer: optionId
        });
      } catch (err) {
        // Optional: toast.error("Failed to save answer");
      }
    }
  };

  const handleBookmark = async () => {
    const question = questions[currentQuestionIndex];
    if (!question || !question.id) return;

    const currentlyBookmarked = bookmarks[currentQuestionIndex];

    // Optimistic update
    setBookmarks(prev => ({ ...prev, [currentQuestionIndex]: !currentlyBookmarked }));

    try {
      if (currentlyBookmarked) {
        await httprequest.delete(`/api/v1/questions/${question.id}/bookmark`);
        toast.success("Bookmark removed");
      } else {
        await httprequest.post(`/api/v1/questions/${question.id}/bookmark`);
        toast.success("Question bookmarked");
      }
    } catch (err) {
      // Revert on fail
      setBookmarks(prev => ({ ...prev, [currentQuestionIndex]: currentlyBookmarked }));
      toast.error("Failed to update bookmark");
    }
  };

  const handleReport = async () => {
    const question = questions[currentQuestionIndex];
    if (!question || !question.id || !reportReason) return;
    try {
      setIsReporting(true);
      await httprequest.post(`/api/v1/questions/${question.id}/report`, {
        reason: "user_reported",
        description: reportReason
      });
      toast.success("Question reported. Thank you!");
      setReportModalOpen(false);
      setReportReason("");
    } catch (err) {
      toast.error("Failed to submit report");
    } finally {
      setIsReporting(false);
    }
  };

  const handleAskAI = async (type = "explain") => {
    const question = questions[currentQuestionIndex];
    if (!question || !question.id) return;

    const payload = { type };
    if (type === "why_wrong") {
      const userAnswer = answeredQuestions[currentQuestionIndex];
      if (!userAnswer) {
        toast.error("Please answer the question before asking why you are wrong.");

        return;
      }
      payload.user_answer = userAnswer;
    }

    try {
      setAiExplanation(null); // Clear previous prompt
      setAiLoading(true);
      const res = await httprequest.post(`/api/v1/questions/${question.id}/ai`, payload);
      const aiData = res?.data?.data;
      if (aiData) {
        setAiExplanation(aiData.response || aiData.explanation || (typeof aiData === "string" ? aiData : "No response provided."));
      }
    } catch (err) {
      toast.error("Failed to fetch AI response");
    } finally {
      setAiLoading(false);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handlePageClick = (index) => {
    setCurrentQuestionIndex(index);
  };

  if (loading || isSubmitting) {
    return <PageLoader />;
  }

  if (totalQuestions === 0) {
    return (
      <Layout>
        <div style={{ padding: "40px", textAlign: "center" }}>No questions found in this session.</div>
      </Layout>
    );
  }

  // Build the mock array for iteration
  const paginationArray = Array.from({ length: totalQuestions }, (_, i) => i);
  const currentQuestion = questions[currentQuestionIndex];

  // Extract text safely depending on whether question is an object or string
  let questionText = typeof currentQuestion?.question === "object" ? currentQuestion?.question?.text : currentQuestion?.question;
  questionText = questionText || currentQuestion?.question_text || currentQuestion?.questionText || currentQuestion?.text || "";

  // Extract options based on backend format (array vs dictionary object)
  let rawOptions = typeof currentQuestion?.question === "object" ? currentQuestion?.question?.options : currentQuestion?.options;
  rawOptions = rawOptions || [];

  // Normalize options shape if it's an array of strings vs object with keys (A, B, C)
  let normalizedOptions = [];
  if (Array.isArray(rawOptions)) {
    normalizedOptions = rawOptions.map((opt, i) => {
      if (typeof opt === "string") {
        return { id: String.fromCharCode(65 + i), text: opt };
      }

      return opt;
    });
  } else if (typeof rawOptions === "object") {
    // Map object format {"A": "Option1", "B": "Option2"} into array
    normalizedOptions = Object.entries(rawOptions).map(([key, value]) => {
      return { id: key, text: value === null ? "" : (typeof value === "object" ? value?.text || "" : value) };
    });
  }

  return (
    <Layout
      children={
        <>
          <div className="white_bg">
            <div className={classes.quizTopBar}>
              <h2>{sessionData?.session?.filter?.subject || "Practice Question"} - {sessionData?.session?.filter?.year || ""}</h2>
              <div style={{ display: "flex", gap: "10px", width: "300px" }}>
                <Button
                  variant="primary"
                  buttonName="Submit Exam"
                  buttonSize="full"
                  color="btndefault"
                  onClick={handleSubmitExam}
                />
              </div>
            </div>

            <div className={classes.quizContentWrapper}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={9}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                    <div className={classes.questionTracker} style={{ marginBottom: 0 }}>
                      Question {currentQuestionIndex + 1} of {totalQuestions}
                    </div>
                    <div style={{ display: "flex", gap: "15px" }}>
                      <button
                        onClick={handleBookmark}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          display: "flex", alignItems: "center", gap: "5px", color: bookmarks[currentQuestionIndex] ? "#0065FF" : "#667085"
                        }}
                      >
                        <Icon
                          icon={bookmarks[currentQuestionIndex] ? "lucide:bookmark-check" : "lucide:bookmark"}
                          width="20"
                        />
                        <span style={{ fontSize: "14px", fontWeight: "500" }}>
                          {bookmarks[currentQuestionIndex] ? "Bookmarked" : "Bookmark"}
                        </span>
                      </button>
                      <button
                        onClick={() => setReportModalOpen(true)}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          display: "flex", alignItems: "center", gap: "5px", color: "#667085"
                        }}
                      >
                        <Icon icon="mdi:flag-variant-outline" width="20" />
                        <span style={{ fontSize: "14px", fontWeight: "500" }}>Report</span>
                      </button>
                    </div>
                  </div>

                  <div className={classes.questionText} dangerouslySetInnerHTML={{ __html: questionText }} />

                  <div className={classes.optionsList}>
                    {normalizedOptions.map((option) => {
                      const isActive = currentSelectedAnswer === option.id;
                      let itemClass = `${classes.optionItem} ${isActive ? classes.optionItemActive : ""}`;

                      // Only apply practice instant feedback if they have selected an answer
                      if (sessionData?.session?.mode === "practice" && currentSelectedAnswer && currentQuestion?.question?.correct_answer) {
                        const isThisOptionCorrect = option.id === currentQuestion.question.correct_answer;
                        const isThisOptionSelected = isActive;

                        if (isThisOptionCorrect) {
                          itemClass = `${classes.optionItem} ${classes.optionItemCorrect}`;
                        } else if (isThisOptionSelected && !isThisOptionCorrect) {
                          itemClass = `${classes.optionItem} ${classes.optionItemIncorrect}`;
                        } else {
                          itemClass = classes.optionItem;
                        }
                      }

                      return (
                        <div
                          key={option.id}
                          className={itemClass}
                          style={examPaused ? { backgroundColor: "#EAECF0", color: "#98A2B3", pointerEvents: "none", cursor: "not-allowed" } : {}}
                          onClick={() => {
                            if (examPaused) return; // Prevent clicking when paused
                            if (sessionData?.session?.mode === "practice" && currentSelectedAnswer) return; // Lock answer in instant-feedback mode
                            handleSelectOption(option.id);
                          }}
                        >
                          <span className={classes.optionLabel}>{option.id}.</span>
                          <span className={classes.optionText} dangerouslySetInnerHTML={{ __html: option.text }} />
                        </div>
                      );
                    })}
                  </div>

                  {sessionData?.session?.mode === "practice" && currentSelectedAnswer && currentQuestion?.question?.correct_answer && (
                    <div style={{ marginTop: "20px", fontSize: "16px", fontWeight: "600", color: currentSelectedAnswer === currentQuestion.question.correct_answer ? "#16B364" : "#1F2329", display: "flex", alignItems: "center", gap: "5px" }}>
                      {currentSelectedAnswer === currentQuestion.question.correct_answer ? (
                        <>Correct! The answer is {currentSelectedAnswer}.</>
                      ) : (
                        <>
                          Wrong! Correct answer is {currentQuestion.question.correct_answer}. <span style={{ color: "#1F2329" }} dangerouslySetInnerHTML={{ __html: normalizedOptions.find(o => o.id === currentQuestion.question.correct_answer)?.text || "" }} />
                        </>
                      )}
                    </div>
                  )}
                </Grid>

                <Grid item xs={12} md={3}>
                  {sessionData?.session?.mode === "exam" && (
                    <div className={classes.timercont}>
                      <h5>Timer</h5>
                      <div className={classes.times}>
                        <CircularProgressbar
                          counterClockwise
                          background
                          value={percentage}
                          text={timer}
                          strokeWidth={5}
                          styles={buildStyles({
                            textColor: "#000000",
                            pathColor: percentage >= 75 ? "#D92D20" : percentage >= 50 ? "#F79009" : "#28AE00",
                            trailColor: "#FFF",
                            textSize: "20px",
                            strokeLinecap: "butt",
                            textWeight: "400"
                          })}
                        />
                      </div>
                      <div style={{ marginTop: "15px", display: "flex", justifyContent: "center" }}>
                        <Button
                          variant="primaryoutline"
                          buttonName={examPaused ? "Resume Exam" : "Pause Exam"}
                          buttonSize="md"
                          color="btnfontprimary"
                          onClick={togglePauseResume}
                        />
                      </div>
                    </div>
                  )}

                  {sessionData?.session?.mode === "practice" && (
                    <div className={classes.askAiContainer}>
                      <img src={robotAiImg} alt="Ask AI Robot" />
                      <h3
                        onClick={() => setIsAiDrawerOpen(true)}
                        style={{ cursor: "pointer", color: "#0065FF", textDecoration: "underline" }}
                      >
                        Ask AI
                      </h3>
                    </div>
                  )}
                </Grid>
              </Grid>
            </div>

            {/* Pagination Footer */}
            <div className={classes.quizPaginationWrapper}>
              <button
                onClick={handlePrevious}
                className={`${classes.quizPageBtn} ${classes.quizPageBtnText}`}
                disabled={currentQuestionIndex === 0 || examPaused}
                style={examPaused ? { pointerEvents: "none", opacity: 0.5 } : {}}
              >
               Previous
              </button>

              <div className={classes.quizPageNumbers}>
                {paginationArray.map((index) => {
                  const isCurrent = index === currentQuestionIndex;
                  const isAnswered = answeredQuestions[index] != null;

                  return (
                    <button
                      key={index}
                      onClick={() => handlePageClick(index)}
                      disabled={examPaused}
                      className={`${classes.quizPageBtn} ${classes.quizPageBtnNumber} ${
                        isCurrent ? classes.quizPageNumberCurrent : ""
                      } ${!isCurrent && isAnswered ? classes.quizPageNumberAnswered : ""}`}
                      style={examPaused ? { pointerEvents: "none", opacity: 0.5 } : {}}
                    >
                      {index + 1}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleNext}
                className={`${classes.quizPageBtn} ${classes.quizPageBtnText} ${classes.quizPageBtnNext}`}
                disabled={currentQuestionIndex === totalQuestions - 1 || examPaused}
                style={examPaused ? { pointerEvents: "none", opacity: 0.5 } : {}}
              >
               Next
              </button>
            </div>
          </div>
          <Modal
            isopen={reportModalOpen}
            closeModal={() => setReportModalOpen(false)}
            title="Report Question"
            subtitle="Please tell us what's wrong with this question."
            modalContent={
              <div style={{ padding: "10px" }}>
                <InputGroup
                  inputType="textarea"
                  inputName="reportReason"
                  placeHolder="E.g. Typo in option A, wrong answer provided, unreadable text..."
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  style={{ minHeight: "100px", width: "100%" }}
                />
              </div>
            }
            modalFooter={
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <Button
                  variant="primaryoutline"
                  buttonName="Cancel"
                  buttonSize="full"
                  color="btnfontprimary"
                  onClick={() => setReportModalOpen(false)}
                />
                <Button
                  variant="primary"
                  buttonName={isReporting ? "Reporting..." : "Submit Report"}
                  buttonSize="full"
                  color="btndefault"
                  onClick={handleReport}
                  disabled={!reportReason.trim() || isReporting}
                />
              </div>
            }
          />
          <RightDrawer
            open={isAiDrawerOpen}
            onClose={() => setIsAiDrawerOpen(false)}
            title="Ask AI"
          >
            <p style={{ margin: "0 0 20px 0", color: "#667085", fontSize: "14px" }}>
              Get help on this question (Practice mode)
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "20px" }}>
              <button
                onClick={() => handleAskAI("explain")}
                className={classes.aiActionBtn}
              >
                <Icon icon="mdi:text-box-search-outline" color="#0065FF" width="20" /> Explain the Question
              </button>

              <button
                onClick={() => handleAskAI("hint")}
                className={classes.aiActionBtn}
              >
                <Icon icon="mdi:lightbulb-on-outline" color="#FACC15" width="20" /> Give me an hint.
              </button>

              <button
                onClick={() => handleAskAI("why_wrong")}
                className={classes.aiActionBtn}
              >
                <Icon icon="mdi:help-circle-outline" color="#D92D20" width="20" /> Why is my Answer Wrong?
              </button>
            </div>

            {(aiLoading || aiExplanation) && (
              <div style={{ padding: "20px", border: "1px solid #EAECF0", borderRadius: "8px", background: "#FFF" }}>
                {aiLoading ? (
                  <div style={{ textAlign: "center", padding: "20px", color: "#667085" }}>
                    <Icon icon="eos-icons:loading" width="30" />
                    <p style={{ marginTop: "10px", fontSize: "14px" }}>Generating insight...</p>
                  </div>
                ) : (
                  <div style={{ fontSize: "14px", lineHeight: "1.5", color: "#333" }} dangerouslySetInnerHTML={{ __html: aiExplanation }} />
                )}
              </div>
            )}

            <div style={{ marginTop: "20px" }}>
              <Button
                variant="primary"
                buttonName="Close"
                buttonSize="full"
                color="btndefault"
                onClick={() => setIsAiDrawerOpen(false)}
              />
            </div>
          </RightDrawer>
        </>
      }
    />
  );
};

export default PracticeQuiz;
